import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, Image, Images, Pencil, SquarePen, Type } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import '../../../shared/styles/Orchestrator.css';

const LAYER_TYPE_ICON = {
  text: Type,
  draw: Pencil,
  image: Image,
};

function LayerList({ layers, onToggleVisible, onEdit, onToggleGroupVisible, onStartFlip, activeFlipLayerId, onReorder, drawingLayerId, onToggleDraw }) {
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const rowRefs = useRef(new Map());

  const toggleCollapse = (grp) => {
    setCollapsedGroups((prev) => ({ ...prev, [grp]: !prev[grp] }));
  };

  const grouped = {};
  const ungrouped = [];
  layers.forEach((layer) => {
    const grp = (layer.group || '').trim();
    if (grp) {
      if (!grouped[grp]) grouped[grp] = [];
      grouped[grp].push(layer);
    } else {
      ungrouped.push(layer);
    }
  });

  const activeFlipLayer = layers.find((layer) => layer.id === activeFlipLayerId);
  const activeFlipGroup = (activeFlipLayer?.group || '').trim();

  useEffect(() => {
    if (!activeFlipGroup || !collapsedGroups[activeFlipGroup]) return;
    setCollapsedGroups((previous) => ({ ...previous, [activeFlipGroup]: false }));
  }, [activeFlipGroup, collapsedGroups]);

  useEffect(() => {
    if (!activeFlipLayerId) return;
    const rowEl = rowRefs.current.get(activeFlipLayerId);
    if (!rowEl) return;
    const scroller = rowEl.closest('.orchestrator_scrollable_content') || rowEl.closest('.orchestrate_panel');
    if (!scroller) return;

    const rowRect = rowEl.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const targetTop = scroller.scrollTop + (rowRect.top - scrollerRect.top)
      - (scroller.clientHeight / 2 - rowRect.height / 2);

    scroller.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  }, [activeFlipLayerId, collapsedGroups]);

  if (!layers.length) {
    return <div className='layer_list no_entity'>No layers yet.</div>;
  }

  const clearDrag = () => { setDragId(null); setDragOverId(null); };

  const handleDrop = (targetItemId, targetGroupName) => {
    if (!dragId || !onReorder) { clearDrag(); return; }
    clearDrag();
    if (dragId === targetItemId) return;
    const allItems = [...Object.values(grouped).flat(), ...ungrouped];
    const dragged = allItems.find((l) => l.id === dragId);
    if (!dragged) return;
    const rest = allItems.filter((l) => l.id !== dragId);
    const moved = { ...dragged, group: targetGroupName };
    if (targetItemId != null) {
      const idx = rest.findIndex((l) => l.id === targetItemId);
      rest.splice(idx >= 0 ? idx : rest.length, 0, moved);
    } else {
      const grpItems = rest.filter((l) => (l.group || '').trim() === targetGroupName);
      if (grpItems.length > 0) {
        const lastIdx = rest.findIndex((l) => l.id === grpItems[grpItems.length - 1].id);
        rest.splice(lastIdx + 1, 0, moved);
      } else {
        rest.push(moved);
      }
    }
    onReorder(rest);
  };

  const renderLayer = (layer) => {
    const layerType = layer.type || 'image';
    const TypeIcon = LAYER_TYPE_ICON[layerType] || Image;
    const isActiveDrawLayer = layerType === 'draw' && drawingLayerId === layer.id;
    return (
    <div
      key={layer.id}
      className={`layer_row${activeFlipLayerId === layer.id ? ' layer_row_selected' : ''}${dragId === layer.id ? ' drag_dragging' : ''}${dragOverId === layer.id ? ' drag_over_before' : ''}`}
      ref={(node) => {
        if (node) rowRefs.current.set(layer.id, node);
        else rowRefs.current.delete(layer.id);
      }}
      draggable
      onDragStart={(e) => { e.stopPropagation(); setDragId(layer.id); setDragOverId(null); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(layer.id); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(layer.id, (layer.group || '').trim()); }}
      onDragEnd={clearDrag}
    >
      <span className='drag_handle'><GripVertical size={14} /></span>
      <span className='layer_type_badge' title={layerType}><TypeIcon size={12} /></span>
      <span className='token_name' title={layer.name}>{layer.name}</span>
      <div style={{ display: 'flex', gap: '4px' }}>
        {layerType === 'draw' && typeof onToggleDraw === 'function' && (
          <Button
            icon={Pencil}
            type={isActiveDrawLayer ? 'tomato' : 'black-white'}
            condensed
            description={isActiveDrawLayer ? 'Exit Draw Mode' : 'Draw Mode'}
            onPress={() => onToggleDraw(layer.id)}
          />
        )}
        <Button
          icon={layer.visible === false ? 'show' : 'hide'}
          type={layer.visible === false ? 'black-white' : 'tomato'}
          condensed
          description={layer.visible === false ? 'Show' : 'Hide'}
          onPress={() => onToggleVisible(layer.id)}
        />
        <Button
          icon={SquarePen}
          type='black-white'
          condensed
          description='Edit'
          onPress={() => onEdit(layer)}
        />
      </div>
    </div>
    );
  };

  return (
    <div className='layer_list' style={{ marginTop: '-4px' }}>
      {Object.entries(grouped).map(([grp, grpLayers]) => {
        const isCollapsed = !!collapsedGroups[grp];
        const allVisible = grpLayers.every((l) => l.visible !== false);
        const isGrpOver = dragOverId === `__grp_${grp}`;
        return (
          <div key={grp} className='group_section'>
            <div
              className={`group_header${isGrpOver ? ' drag_over_group' : ''}`}
              onClick={() => toggleCollapse(grp)}
              role='button'
              tabIndex={0}
              aria-expanded={!isCollapsed}
              onKeyDown={(e) => {
                if (e.key === ' ') e.preventDefault();
                if (e.key === 'Enter' || e.key === ' ') toggleCollapse(grp);
              }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(`__grp_${grp}`); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(null, grp); }}
            >
              <button className='group_collapse_btn' tabIndex={-1} aria-hidden='true'>
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </button>
              <span className='group_name' title={grp}>{grp}</span>
              <div style={{ display: 'flex', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
                <Button
                  icon={Images}
                  type={grpLayers.some((layer) => layer.id === activeFlipLayerId) ? 'tomato' : 'black-white'}
                  condensed
                  description={grpLayers.some((layer) => layer.id === activeFlipLayerId) ? 'Exit Flip Mode' : 'Flip Through Group'}
                  onPress={() => onStartFlip?.(grp)}
                />
                <Button
                  icon={allVisible ? 'hide' : 'show'}
                  type={allVisible ? 'tomato' : 'black-white'}
                  condensed
                  description={allVisible ? 'Hide Group' : 'Show Group'}
                  onPress={() => onToggleGroupVisible(grp)}
                />
              </div>
            </div>
            {!isCollapsed && (
              <div
                className='group_items'
                style={{paddingTop: '0px'}}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(null, grp); }}
              >
                {grpLayers.map(renderLayer)}
              </div>
            )}
          </div>
        );
      })}
      <div
        className='ungrouped_zone'
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(null, ''); }}
      >
        {ungrouped.map(renderLayer)}
      </div>
    </div>
  );
}

export default LayerList;
